<!-- View for browsing all packages -->
<template>
	<div class="relative z-20 flex h-full w-full pt-14">
		<!-- Left Column -->
		<div class="w-fit bg-white py-8 pr-[2.5rem] pl-4 shadow-lg">
			<ul
				class="text-md text-secondary-900 flex flex-col gap-3 text-center font-serif whitespace-nowrap uppercase *:min-w-31.5 *:cursor-pointer *:border-b *:border-slate-300 *:pb-2 *:text-left *:uppercase *:italic"
			>
				<li
					v-if="showRecommended"
					class="hover:text-accent-300 hover:font-semibold"
					:class="{
						'text-accent-400 font-semibold': selectedType === 'Recommended',
					}"
					@click="selectCategory('Recommended')"
				>
					{{ t('packageSlide.recommended') }}
				</li>
				<li
					class="hover:text-accent-300 hover:font-semibold"
					:class="{
						'text-accent-400 font-semibold':
							selectedType === PbSubcategory.PackageBurial,
					}"
					@click="selectCategory(PbSubcategory.PackageBurial)"
				>
					{{ t('packageSlide.burial') }}
				</li>
				<li
					class="hover:text-accent-300 hover:font-semibold"
					:class="{
						'text-accent-400 font-semibold':
							selectedType === PbSubcategory.Cremation,
					}"
					@click="selectCategory(PbSubcategory.Cremation)"
				>
					{{ t('packageSlide.cremation') }}
				</li>
			</ul>
		</div>

		<!-- Right Column -->
		<div
			ref="scrollRef"
			class="flex-1 divide-y divide-gray-200 overflow-auto bg-white pr-32 *:py-8 first:pt-0 last:pb-0"
		>
			<template v-if="selectedType === 'Recommended'">
				<Package
					v-for="rec in groupedPackageRecommendations"
					:key="rec.pkg.id"
					:pkg="rec.pkg"
					:recommendation="rec"
					@select="selectPackage"
				/>
			</template>
			<template v-else>
				<Package
					v-for="pkg in packagesBySelection"
					:key="pkg.id"
					:pkg="pkg"
					@select="selectPackage"
				/>
			</template>
		</div>

		<!-- Overlay -->

		<Transition
			:enter-active-class="'transition duration-300 ease-out'"
			:enter-from-class="'translate-x-full opacity-0'"
			:enter-to-class="'translate-x-0 opacity-100'"
			:leave-active-class="
				leaveWithSlide ? 'transition duration-300 ease-in' : ''
			"
			:leave-from-class="leaveWithSlide ? 'translate-x-0 opacity-100' : ''"
			:leave-to-class="leaveWithSlide ? 'translate-x-full opacity-0' : ''"
		>
			<div
				v-if="selectedPackage"
				class="pointer-events-auto absolute top-0 right-0 bottom-0 left-0 z-20 flex justify-center bg-white pt-24 will-change-transform"
			>
				<OverlayPackageDetails
					:pkg="selectedPackage"
					:blok="blok"
					@close="handleOverlayClose"
					@close-with-slide="handleOverlayClose(true)"
				/>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import type { PbLineItemPackage } from '@/apiTypes'
import type Plan from '@/classes/Plan'
import OverlayPackageDetails from '@/components/packages/OverlayPackageDetails.vue'
import Package from '@/components/packages/Package.vue'
import { Disposition } from '@/enums/Disposition'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { usePresentationLocale } from '@/i18n'
import {
	getPackageRecommendations,
	type PackageRecommendation,
	Reason,
} from '@/mixins/packageRecommendations'
import type { PackagesSlideStoryblok } from '@/sbTypes'
import { useSessionStore } from '@/stores/session'
import {
	computed,
	onMounted,
	ref,
	watch,
	type PropType,
	type VNodeRef,
} from 'vue'

const selectedPackage = ref<PbLineItemPackage | null>(null)

// Selects a package to be displayed in the overlay
const selectPackage = (pkg: PbLineItemPackage) => {
	selectedPackage.value = pkg
}

const { t } = usePresentationLocale()

type PackageType =
	| 'Recommended'
	| PbSubcategory.PackageBurial
	| PbSubcategory.Cremation

export type GroupedRecommendation = {
	pkg: PbLineItemPackage
	plans: Plan[]
	reasons: Set<Reason>
}

const sessionStore = useSessionStore()
const selectedType = ref<PackageType>(PbSubcategory.PackageBurial)
const scrollRef = ref<VNodeRef | undefined>(undefined)

const props = defineProps({
	active: Boolean,
	blok: Object as PropType<PackagesSlideStoryblok>,
})

const leaveWithSlide = ref(false)

/**
 * Close the overlay and reset the selected package.
 * If `slide` is true, use a slide transition when closing the overlay.
 */
const handleOverlayClose = (slide = false) => {
	leaveWithSlide.value = slide
	selectedPackage.value = null
}

// Select a package category
const selectCategory = (type: PackageType) => {
	selectedType.value = type
}

/** All packages */
const packages = computed(() => sessionStore.activePriceBook?.packages)

/**Filter packages by selected type */
const packagesBySelection = computed(() => {
	return packages.value?.filter((pkg) => pkg.subcategory === selectedType.value)
})

/** List of package recommendations w/ the associated plan */
const packageRecommendations = computed<PackageRecommendation[]>(() =>
	sessionStore.activePriceBook
		? getPackageRecommendations(sessionStore.activePriceBook)
		: []
)

/** List of recommended packages w/ a list of plans and reasons */
const groupedPackageRecommendations = computed(() => {
	const packages: GroupedRecommendation[] = []

	for (const rec of packageRecommendations.value) {
		const match = packages.find((p) => p.pkg.id === rec.pkg.id)
		if (match) {
			match.plans.push(rec.plan)
			rec.reasons.forEach((reason) => match.reasons.add(reason))
			continue
		}

		packages.push({
			pkg: rec.pkg,
			plans: [rec.plan],
			reasons: new Set<Reason>(rec.reasons),
		})
	}

	return packages
})

/** Should we show package recommendations? */
const showRecommended = computed(
	() => packageRecommendations.value.length === sessionStore.activePlans.length
)

onMounted(() => {
	selectedType.value = getDefaultSelectedType()
})

// When the selected type changes, reset the scroll position
watch(selectedType, () => {
	if (scrollRef.value) {
		scrollRef.value.scrollTop = 0
	} else {
		console.error('Scroll ref not found')
	}
})

// When the slide becomes active, select a category based on Plan settings
watch(
	() => props.active,
	(active) => {
		if (active) {
			selectedType.value = getDefaultSelectedType()
		}
	}
)

function getDefaultSelectedType() {
	const plans = sessionStore.activePlans

	if (showRecommended.value) {
		return 'Recommended'
	}

	if (plans?.some((plan) => plan.burialOrCremation === Disposition.Burial)) {
		return PbSubcategory.PackageBurial
	}

	if (plans?.some((plan) => plan.burialOrCremation === Disposition.Cremation)) {
		return PbSubcategory.Cremation
	}

	return PbSubcategory.PackageBurial
}
</script>
